import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { OC, FONT } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import { isEmployerRole, isAdminRole } from '../constants/index.js';
import { LogoNav, Button, Icon, Avatar } from './ui.js';

// ========================================
// ✅ UPDATED AccountMenu with "Back to Opportunities"
function AccountMenu() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [open, setOpen] = useState(false);

    const go = (name, params) => { setOpen(false); s.go(name, params); };

    const menuItems = s.isEmployerMode ? [
        { label: 'Dashboard', icon: 'chart', action: () => go('employers') },
        { 
            label: 'Back to Opportunities', 
            icon: 'arrowLeft', 
            action: () => { 
                setOpen(false); 
                s.setEmployerMode(false); 
                s.go('feed'); 
                s.showToast('Switched back to job seeker'); 
            } 
        },
        // ✅ FIXED: employers route ke saath tab parameter
        { 
            label: 'Company Profile', 
            icon: 'building', 
            action: () => {
                setOpen(false);
                s.go('employers', { tab: 'profile' });  // 👈 Direct dashboard ka profile tab
                s.showToast('Company Profile', 'building');
            } 
        },
        { label: 'Log out', icon: 'x', action: () => { setOpen(false); s.logout(); s.go('home'); s.showToast('Logged out'); }, danger: true },
    ] : [
        { label: 'Saved', icon: 'bookmark', action: () => go('saved') },
        { label: 'Alerts', icon: 'bell', action: () => go('notifications') },
        { label: 'Application tracker', icon: 'briefcase', action: () => go('applications') },
        { 
            label: 'Hire Employers', 
            icon: 'briefcase', 
            action: async () => { 
                setOpen(false);
                try {
                    if (!s.authed) {
                        s.go('login');
                        return;
                    }
                    if (!isEmployerRole(s.user.role)) {
                        await s.registerAsEmployer();
                    }
                    s.setEmployerMode(true);
                    s.go('employers');
                    s.showToast('Switched to Hiring Employer', 'briefcase');
                } catch (err) {
                    s.showToast(err.message || 'Could not switch to employer', 'x');
                }
            } 
        },
        { label: 'Settings', icon: 'user', action: () => go('account', { tab: 'settings' }) },
        { label: 'Log out', icon: 'x', action: () => { setOpen(false); s.logout(); s.go('home'); s.showToast('Logged out'); }, danger: true },
    ];

    return React.createElement('div', { style: { position: 'relative' } },
        React.createElement('button', {
            onClick: () => setOpen(o => !o),
            style: { background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999 }
        },
            React.createElement(Avatar, { name: s.user.name, size: 38, theme: s.theme }),
            React.createElement(Icon, { name: 'chevDown', size: 15, color: t.text.secondary })
        ),
        open ? [
            React.createElement('div', { key: 'overlay', onClick: () => setOpen(false), style: { position: 'fixed', inset: 0, zIndex: 50 } }),
            React.createElement('div', { key: 'menu', style: { position: 'absolute', top: 50, right: 0, width: 232, zIndex: 60, background: t.bg.page, border: `1px solid ${t.border.base}`, borderRadius: 14, boxShadow: t.shadow.lg, overflow: 'hidden', padding: 6 } },
                React.createElement('div', { style: { padding: '10px 12px 8px' } },
                    React.createElement('div', { style: { fontFamily: FONT.display, fontWeight: 700, fontSize: 14, color: t.text.primary } }, s.user.name),
                    React.createElement('div', { style: { fontSize: 12, color: t.text.secondary, marginTop: 1 } }, s.user.email)
                ),
                React.createElement('div', { style: { borderTop: `1px solid ${t.border.base}`, padding: '5px 0', marginTop: 4 } },
                    menuItems.map(item =>
                        React.createElement('button', {
                            key: item.label, onClick: item.action,
                            style: {
                                width: '100%', display: 'flex', alignItems: 'center', gap: 11,
                                padding: '9px 10px', background: 'none', border: 'none',
                                cursor: 'pointer', borderRadius: 9, textAlign: 'left',
                                fontFamily: FONT.body, fontSize: 13.5, fontWeight: 500,
                                color: item.danger ? OC.urgent : t.text.primary,
                            }
                        },
                            React.createElement(Icon, { name: item.icon, size: 16, color: item.danger ? OC.urgent : t.text.secondary }),
                            item.label
                        )
                    )
                )
            )
        ] : null
    );
}

function AppNav() {
    const s = useStore();
    const t = useTheme(s.theme);

    const navItems = s.isEmployerMode ? [
        { key: 'home', label: 'Home' },
        { key: 'postJob', label: 'Post an opportunity' },
        { key: 'employers', label: 'Dashboard' },
        { key: 'community', label: 'Community' },
        { key: 'about', label: 'About' },
        
    ] : [
        { key: 'home', label: 'Home' },
        { key: 'feed', label: 'Opportunities' },
        { key: 'community', label: 'Community' },
        { key: 'resources', label: 'Resources' },
        { key: 'about', label: 'About' },
    ];

    const iconBtnStyle = (on) => ({
        background: 'transparent',
        border: `1px solid ${t.border.base}`,
        cursor: 'pointer',
        width: 38,
        height: 38,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: on ? OC.orange : t.text.primary,
        borderRadius: 10,
        position: 'relative',
    });

    const isActive = (key) => {
        if (key === 'home') return s.route.name === 'home';
        if (key === 'feed') return s.route.name === 'feed' || s.route.name === 'detail' || s.route.name === 'search';
        if (key === 'employers') return s.route.name === 'employers';
        if (key === 'postJob') return s.route.name === 'postJob';
        if (key === 'community') return s.route.name === 'community';
        if (key === 'resources') return s.route.name === 'resources' || s.route.name === 'guideDetail';
        return s.route.name === key;
    };

    // ✅ In employer mode OR viewing a job detail: show Dashboard, hide Search & Save
    const isEmployerView = s.isEmployerMode || s.route.name === 'detail';

    return React.createElement('nav', {
        style: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 32px', height: 68,
            borderBottom: `1px solid ${t.border.base}`,
            background: t.bg.page, position: 'sticky', top: 0, zIndex: 40,
        }
    },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 38 } },
            React.createElement('button', {
                onClick: () => s.go('home'),
                style: { background: 'none', border: 'none', cursor: 'pointer', padding: 0 }
            }, React.createElement(LogoNav, { theme: s.theme, height: 27 })),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                navItems.map(it =>
                    React.createElement('button', {
                        key: it.key, onClick: () => s.go(it.key),
                        style: {
                            background: isActive(it.key) ? (s.theme === 'dark' ? 'rgba(249,115,22,.14)' : OC.orangeMuted) : 'transparent',
                            border: 'none', cursor: 'pointer', borderRadius: 9,
                            padding: '8px 13px', fontFamily: FONT.body, fontSize: 14,
                            fontWeight: isActive(it.key) ? 700 : 500,
                            color: isActive(it.key) ? OC.orange : t.text.secondary,
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                        }
                    }, it.label)
                )
            )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
            // ✅ In Employer Mode OR viewing detail: Show Dashboard, hide Search & Save
            isEmployerView ? React.createElement('button', {
                onClick: () => s.go('employers'), title: 'Dashboard',
                style: iconBtnStyle(s.route.name === 'employers')
            }, React.createElement(Icon, { name: 'chart', size: 18 })) : [
                React.createElement('button', {
                    key: 'search',
                    onClick: () => s.go('search'), title: 'Search',
                    style: iconBtnStyle(s.route.name === 'search')
                }, React.createElement(Icon, { name: 'search', size: 18 })),
                React.createElement('button', {
                    key: 'saved',
                    onClick: () => s.go('saved'), title: 'Saved',
                    style: iconBtnStyle(s.route.name === 'saved')
                },
                    React.createElement(Icon, { name: 'bookmark', size: 18 }),
                    s.saved.length ? React.createElement('span', {
                        style: {
                            position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16,
                            padding: '0 4px', borderRadius: 8, background: OC.orange,
                            color: '#fff', fontSize: 10, fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            border: `2px solid ${t.bg.page}`,
                        }
                    }, s.saved.length) : null
                ),
                React.createElement('button', {
                    key: 'applications',
                    onClick: () => s.go('applications'), title: 'Application tracker',
                    style: iconBtnStyle(s.route.name === 'applications')
                },
                    React.createElement(Icon, { name: 'briefcase', size: 18 }),
                    s.applied.length ? React.createElement('span', {
                        style: {
                            position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16,
                            padding: '0 4px', borderRadius: 8, background: OC.orange,
                            color: '#fff', fontSize: 10, fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            border: `2px solid ${t.bg.page}`,
                        }
                    }, s.applied.length) : null
                ),
            ],
            React.createElement('button', {
                onClick: s.toggleTheme, title: 'Toggle light / dark',
                style: iconBtnStyle(false)
            }, React.createElement(Icon, { name: s.theme === 'dark' ? 'sun' : 'moon', size: 18 })),
            s.authed && isAdminRole(s.user.role) ? React.createElement('button', {
                key: 'admin',
                onClick: () => s.go('admin'), title: 'Admin console',
                style: iconBtnStyle(s.route.name === 'admin')
            }, React.createElement(Icon, { name: 'shieldCheck', size: 18 })) : null,
            s.authed ? React.createElement('button', {
                onClick: () => s.go('notifications'), title: 'Notifications',
                style: iconBtnStyle(s.route.name === 'notifications')
            },
                React.createElement(Icon, { name: 'bell', size: 18 }),
                React.createElement('span', {
                    style: {
                        position: 'absolute', top: 8, right: 9,
                        width: 7, height: 7, borderRadius: 4,
                        background: OC.orange, border: `2px solid ${t.bg.page}`,
                    }
                })
            ) : null,
            s.authed ? React.createElement(AccountMenu, null) : [
                React.createElement('button', {
                    key: 'login',
                    onClick: () => {
                        s.go('login');
                        window.history.pushState({}, '', '/auth?mode=login');
                    },
                    style: {
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        fontFamily: FONT.body, fontSize: 14, fontWeight: 600,
                        color: t.text.primary, padding: '8px 12px',
                    }
                }, 'Log in'),
                React.createElement(Button, {
                    key: 'signup',
                    kind: 'primary', size: 'sm', theme: s.theme,
                    onClick: () => {
                        s.go('signup');
                        window.history.pushState({}, '', '/auth?mode=signup');
                    },
                    iconRight: React.createElement(Icon, { name: 'arrowRight', size: 14 })
                }, 'Sign up free')
            ]
        )
    );
}

// --- Footer ---
function Footer() {
    const s = useStore();
    const t = useTheme(s.theme);
    const footerLink = (label) => {
        const map = {
            Jobs: () => s.go('feed', { cat: 'jobs' }),
            Internships: () => s.go('feed', { cat: 'internships' }),
            Scholarships: () => s.go('feed', { cat: 'scholarships' }),
            Admissions: () => s.go('feed', { cat: 'admissions' }),
            Events: () => s.go('feed', { cat: 'events' }),
            'Share an opportunity': () => s.go('community'),
            'WhatsApp channels': () => { s.go('community'); setTimeout(() => document.getElementById('channels')?.scrollIntoView(), 300); },
            Telegram: () => { s.go('community'); setTimeout(() => document.getElementById('channels')?.scrollIntoView(), 300); },
            Newsletter: () => s.go('community'),
            'Volunteer reviewers': () => s.go('community'),
            'Post a role': () => s.go('postJob'),
            'Featured listings': () => s.go('feed'),
            Pricing: () => s.go('about'),
            'Employer login': () => s.go('employers'),
            'Help center': () => s.go('support', { supportSlug: 'help' }),
            Contact: () => s.go('support', { supportSlug: 'contact' }),
            'Terms of service': () => s.go('support', { supportSlug: 'terms' }),
            Privacy: () => s.go('support', { supportSlug: 'privacy' }),
            'Community guidelines': () => s.go('support', { supportSlug: 'guidelines' }),
        };
        return (map[label] || (() => {}))();
    };

    const cols = [
        { title: 'Opportunities', links: ['Jobs', 'Internships', 'Scholarships', 'Admissions', 'Events'] },
        { title: 'Community', links: ['Share an opportunity', 'WhatsApp channels', 'Telegram', 'Newsletter', 'Volunteer reviewers'] },
        { title: 'For employers', links: ['Post a role', 'Featured listings', 'Pricing', 'Employer login'] },
        { title: 'Support', links: ['Help center', 'Contact', 'Terms of service', 'Privacy', 'Community guidelines'] },
    ];

    const publicSiteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PUBLIC_SITE_URL)
        ? String(import.meta.env.VITE_PUBLIC_SITE_URL).trim().replace(/\/$/, '')
        : 'https://opportunitycircle.com';

    const scrollToCommunityChannels = () => {
        footerLink('WhatsApp channels');
    };

    const shareOpportunityCircle = async () => {
        const url = window.location.origin ? `${window.location.origin}/` : `${publicSiteUrl}/`;
        try {
            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share({
                    title: 'Opportunity Circle',
                    text: 'Sharing is caring — discover jobs, scholarships, and more.',
                    url,
                });
                return;
            }
        } catch (err) {
            if (err?.name === 'AbortError') return;
        }
        try {
            await navigator.clipboard.writeText(url);
            s.showToast('Link copied to clipboard', 'check');
        } catch {
            s.showToast('Copy this link: ' + url, 'globe');
        }
    };

    const footerSocialIcons = [
        {
            icon: 'globe',
            title: 'Visit our website',
            action: () => window.open(publicSiteUrl, '_blank', 'noopener,noreferrer'),
        },
        {
            icon: 'send',
            title: 'Regional WhatsApp & Telegram channels',
            action: scrollToCommunityChannels,
        },
        {
            icon: 'mail',
            title: 'Contact us',
            action: () => footerLink('Contact'),
        },
        {
            icon: 'share',
            title: 'Share Opportunity Circle',
            action: shareOpportunityCircle,
        },
        {
            icon: 'bell',
            title: s.authed ? 'Email alerts & saved searches' : 'Sign in for email alerts',
            action: () => {
                if (s.authed) {
                    s.go('notifications');
                } else {
                    s.showToast('Log in to manage email alerts', 'bell');
                    s.go('login');
                }
            },
        },
    ];

    return React.createElement('footer', {
        style: {
            background: s.theme === 'dark' ? t.bg.page : '#F9FAFB',
            borderTop: `1px solid ${t.border.base}`,
            padding: '56px 48px 28px',
            fontFamily: FONT.body,
            marginTop: 'auto'
        }
    },
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1.5fr repeat(4, 1fr)', gap: 40, marginBottom: 40 } },
            React.createElement('div', null,
                React.createElement(LogoNav, { theme: s.theme, height: 30 }),
                React.createElement('p', { style: { fontSize: 13.5, color: t.text.secondary, marginTop: 14, maxWidth: 280, lineHeight: 1.5 } },
                    'Sharing is caring. We centralize life-changing opportunities so no one misses out for lack of access.'
                ),
                React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 18 } },
                    footerSocialIcons.map(({ icon, title, action }) =>
                        React.createElement('button', {
                            key: icon,
                            type: 'button',
                            title,
                            'aria-label': title,
                            onClick: action,
                            style: {
                                width: 32, height: 32, borderRadius: 8,
                                background: t.bg.card, border: `1px solid ${t.border.base}`,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                color: t.text.secondary, cursor: 'pointer', padding: 0,
                            },
                            onMouseEnter: (e) => { e.currentTarget.style.borderColor = OC.orange; e.currentTarget.style.color = OC.orange; },
                            onMouseLeave: (e) => { e.currentTarget.style.borderColor = t.border.base; e.currentTarget.style.color = t.text.secondary; },
                        }, React.createElement(Icon, { name: icon, size: 14 }))
                    )
                )
            ),
            cols.map(col =>
                React.createElement('div', { key: col.title },
                    React.createElement('div', {
                        style: {
                            fontFamily: FONT.mono, fontSize: 11, fontWeight: 600,
                            letterSpacing: '0.14em', textTransform: 'uppercase',
                            color: t.text.secondary, marginBottom: 14,
                        }
                    }, col.title),
                    React.createElement('ul', { style: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 9 } },
                        col.links.map(l =>
                            React.createElement('li', { key: l },
                                React.createElement('button', {
                                    type: 'button',
                                    onClick: () => footerLink(l),
                                    style: {
                                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                        fontSize: 13.5, color: t.text.primary, textAlign: 'left',
                                    }
                                }, l)
                            )
                        )
                    )
                )
            )
        ),
        React.createElement('div', {
            style: {
                paddingTop: 24, borderTop: `1px solid ${t.border.base}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 12.5, color: t.text.secondary,
            }
        },
            React.createElement('div', null, '© 2026 Nebulark · Opportunity Circle · Sharing is Caring'),
            React.createElement('div', { style: { display: 'flex', gap: 16 } },
                React.createElement('span', null, 'opportunitycircle.com'),
                React.createElement('span', null, 'Built by Nebulark')
            )
        )
    );
}

export { AccountMenu, AppNav, Footer };
